import { useEffect, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import { getDeparturesForDate } from '../utils/httpApi';
import { mergeDepartureEvents } from '../utils/departureRows';

/**
 * The day's departure sessions for an airport: loaded once per airport/date, then kept current
 * from the departureForecastUpdate WebSocket. Used by Departures and On Ground.
 */
export default function useDepartureRows(airportIcao, date, departureForecastUpdate) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!airportIcao || !date) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setRows([]);
      try {
        const resp = await getDeparturesForDate(airportIcao, date, getAccessToken());
        if (cancelled) return;
        if (resp.header?.apiStatus === 'OK' || resp.header?.apiStatus === 'SUCCESS') {
          setRows(resp.aircraft || []);
        } else {
          setError(resp.header?.errors?.[0]?.message || 'Failed to load departures');
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Network error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [airportIcao, date]);

  useEffect(() => {
    if (!departureForecastUpdate || !Array.isArray(departureForecastUpdate.events)) return;
    const events = departureForecastUpdate.events.filter(ev => ev.airportIcao === airportIcao);
    if (events.length === 0) return;
    setRows(prev => mergeDepartureEvents(prev, events));
  }, [departureForecastUpdate, airportIcao]);

  return { rows, loading, error };
}
